<script setup lang="ts">
  import { computed, ref } from 'vue';

  import type { CreditMemo, Partner } from '@stuut/api/models';
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ui/components/collapsible';
  import { MonetaryText } from '@ui/components/monetary';

  import CreditMemoStatusBadge from '@/components/credit-memos/CreditMemoStatusBadge.vue';
  import { PartnerQuickView } from '@/components/partners';

  const props = defineProps<{
    creditMemo: CreditMemo;
    partner?: Partner;
  }>();

  const detailsOpen = ref(true);

  const customer = computed(() => props.partner || props.creditMemo.partner);
  const sourceName = computed(
    () =>
      props.creditMemo.linked_integration?.integration?.display_name ||
      props.creditMemo.linked_integration?.integration?.name ||
      props.creditMemo.remote_id,
  );
  const postedOrDueDate = computed(() => props.creditMemo.posted_date || props.creditMemo.due_date);
  const postedOrDueLabel = computed(() => (props.creditMemo.posted_date ? 'Issued' : 'Due'));
</script>

<template>
  <div class="bg-card border p-1">
    <div
      class="border-border outline-border text-card-foreground relative w-full overflow-hidden border outline-1 outline-offset-[3px]"
    >
      <div class="flex items-center justify-between px-6 pb-5 pt-5">
        <div class="flex flex-col gap-y-1.5 min-w-0">
          <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
            Credit Memo
          </p>

          <div class="flex flex-col gap-y-2">
            <p class="text-2xl font-bold leading-none tracking-tight truncate">
              #{{ creditMemo.number || creditMemo.remote_id || creditMemo.id }}
            </p>

            <p v-if="postedOrDueDate" class="text-muted-foreground text-sm">
              {{ postedOrDueLabel }} {{ postedOrDueDate.local().format('MMM D, YYYY') }}
            </p>
          </div>
        </div>

        <CreditMemoStatusBadge :credit-memo="creditMemo" size="sm" />
      </div>

      <div class="border-border border-t-2 border-dotted" />

      <div class="flex flex-col flex-1 gap-y-1.5 px-6 py-5">
        <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
          Billed to
        </p>

        <div class="flex items-baseline justify-between gap-4">
          <PartnerQuickView v-if="customer" :partner="customer" />

          <span v-else class="text-muted-foreground/30">—</span>
        </div>
      </div>

      <div class="border-border border-t-2 border-dotted" />

      <div class="divide-border grid grid-cols-2 divide-x divide-dotted">
        <div class="flex flex-col flex-1 gap-y-1.5 px-6 py-5">
          <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
            Due
          </p>

          <p class="text-sm font-medium">
            <template v-if="creditMemo.due_date">
              {{ creditMemo.due_date.local().format('MMM D, YYYY') }}
            </template>

            <span v-else class="text-muted-foreground/30">—</span>
          </p>
        </div>

        <div class="flex flex-col flex-1 gap-y-1.5 px-6 py-5">
          <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
            Posted
          </p>

          <p class="text-sm font-medium">
            <template v-if="creditMemo.posted_date">
              {{ creditMemo.posted_date.local().format('MMM D, YYYY') }}
            </template>

            <span v-else class="text-muted-foreground/30">—</span>
          </p>
        </div>
      </div>

      <div class="border-border border-t-2 border-dotted" />

      <div class="flex flex-col flex-1 gap-y-1.5 px-6 py-5">
        <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
          Balance
        </p>

        <p class="font-mono text-lg font-semibold tabular-nums tracking-tight">
          <MonetaryText
            :value="creditMemo.remaining_amount"
            :currency="creditMemo.currency"
            major-unit
            :compact="false"
          />

          <span class="text-muted-foreground ml-2 font-mono text-sm font-normal">
            of
            <MonetaryText
              :value="creditMemo.net_amount"
              :currency="creditMemo.currency"
              major-unit
              :compact="false"
            />
          </span>
        </p>
      </div>

      <Collapsible v-model:open="detailsOpen">
        <CollapsibleContent>
          <div v-if="sourceName" class="border-border border-t-2 border-dotted" />

          <div v-if="sourceName" class="flex flex-col flex-1 gap-y-1.5 px-6 py-5">
            <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
              Source
            </p>

            <p class="text-sm font-medium">
              {{ sourceName }}
            </p>
          </div>

          <div v-if="creditMemo.line_items?.length" class="border-border border-t border-dotted">
            <table class="w-full text-xs">
              <thead>
                <tr class="border-border border-y border-dotted">
                  <th
                    class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground px-6 py-2.5 text-left"
                  >
                    Description
                  </th>
                  <th
                    class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground px-4 py-2.5 text-right"
                  >
                    Qty × Price
                  </th>
                  <th
                    class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground px-6 py-2.5 text-right"
                  >
                    Total
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="item in creditMemo.line_items"
                  :key="item.id"
                  class="border-border border-b border-dotted"
                >
                  <td class="px-6 py-2.5">{{ item.description }}</td>
                  <td
                    class="text-muted-foreground whitespace-nowrap px-4 py-2.5 text-right font-mono tabular-nums"
                  >
                    {{ item.quantity }} ×
                    <MonetaryText
                      :value="item.price"
                      :currency="item.currency"
                      major-unit
                      :compact="false"
                    />
                  </td>
                  <td class="whitespace-nowrap px-6 py-2.5 text-right font-mono tabular-nums">
                    <MonetaryText
                      :value="item.net_amount"
                      :currency="item.currency"
                      major-unit
                      :compact="false"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CollapsibleContent>

        <CollapsibleTrigger
          class="border-border text-muted-foreground hover:text-foreground hover:bg-muted/30 flex w-full items-center justify-center gap-1.5 border-t py-2 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors"
          :class="detailsOpen && 'border-t-0'"
        >
          <span>Details</span>
          <i
            class="far fa-chevron-down transition-transform"
            :class="detailsOpen && 'rotate-180'"
          />
        </CollapsibleTrigger>
      </Collapsible>
    </div>
  </div>
</template>
